import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../support/store';
import { initialTileState, Query } from '../../../../types/tileBuilderTypes';
import { getChartKind } from './tilePreviewHelpers';
import TilePreviewControls from './TilePreviewControls';
import TilePreviewFrame from './TilePreviewFrame';
import { useMarginInit } from '../../../../hooks/useTilePreviewMarginInit';
import { useTilePreviewData } from '../../../../hooks/useTilePreviewData';
import { useTileFieldInit } from '../../../../hooks/useTilePreviewFieldInit';

interface TilePreviewProps {
  chartId: string;
  chartType: string;
  chartCategory: number;
}

const TilePreview: React.FC<TilePreviewProps> = ({
                                                   chartId,
                                                   chartType,
                                                   chartCategory,
                                                 }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const { queries } = useSelector(
    (state: RootState) => state.tileBuilder?.[chartId]?.tile || initialTileState
  );

  const {
    allSources = [],
    derivedFields = [],
    resolvedQueriesForPreview = [],
    previewData = [],
  } = useTilePreviewData(chartId);

  const chartKind = useMemo(() => getChartKind(chartType), [chartType]);

  useMarginInit(chartId);
  useTileFieldInit(chartId, chartKind, derivedFields);

  const effectiveQueriesForDateFilter = useMemo<Query[]>(() => {
    const source = resolvedQueriesForPreview?.length ? resolvedQueriesForPreview : queries;
    return (source ?? []) as Query[];
  }, [resolvedQueriesForPreview, queries]);

  const dateFilterEnabled = useMemo(() => {
    return effectiveQueriesForDateFilter.some((query: Query) => query.enableDateFilter);
  }, [effectiveQueriesForDateFilter]);

  const stablePreviewData = useMemo(() => previewData ?? [], [previewData]);
  const stableAllSources = useMemo(() => allSources ?? [], [allSources]);
  const stableDerivedFields = useMemo(() => derivedFields ?? [], [derivedFields]);
  const stableResolvedQueriesForPreview = useMemo<Query[]>(
    () => ((resolvedQueriesForPreview ?? []) as Query[]),
    [resolvedQueriesForPreview]
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 520,
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <TilePreviewControls
        chartKind={chartKind}
        chartId={chartId}
        isSmall={isSmall}
        allSources={stableAllSources}
        dateFilterEnabled={dateFilterEnabled}
        previewData={stablePreviewData}
        derivedFields={stableDerivedFields}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <TilePreviewFrame
          chartId={chartId}
          previewData={stablePreviewData}
          queries={stableResolvedQueriesForPreview}
          chartType={chartType}
          chartCategory={chartCategory}
        />
      </Box>
    </Box>
  );
};

export default TilePreview;